import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { useDeleteCommand } from '$tablet-app/src/app/chat/hooks/useDeleteCommand';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import {
  postDeleteRecordLogEvent,
  postUpdateRecordLogEvent,
} from '$tablet-app/src/app/logging/functions';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { query_client } from '$tablet-app/src/app/query_client';
import { showInfoToast } from '$tablet-app/src/app/toasts';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import { findAndUpdateRecord } from '$tablet-app/src/app/utils/arrays';
import {
  getRecordSetDifference,
  removeRecordByID,
} from '$tablet-app/src/app/utils/records';
import { Alert, createGoalReminder } from '@akin-home/alerts';
import {
  alertQueryKeys,
  useCreateAlertFromModel,
  useDeleteAlert,
  useUpdateAlertFromModel,
} from '@akin-home/alerts/frontend';
import { useListAlertsForGoal } from '@akin-home/alerts/react';
import { FeatureDomain } from '@akin-home/amplify';
import {
  useDeleteGoal,
  useGetGoal,
  useUpdateGoal,
} from '@akin-home/goals/frontend';
import { useHabitatID } from '@akin-home/habitats/react';
import { showSuccessToast } from '@akin-home/toast/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useUpdateGoalFormCommand } from '../../hooks';
import GoalFormScreenView from '../GoalFormScreen/GoalFormScreenView';
import useGoalFormScreen from '../GoalFormScreen/useGoalFormScreen';

const context = {
  canSave: true,
  canDelete: true,
  fields: { title: { type: 'text' }, period: {}, completions_per_period: {} },
};

const parameters = {
  id: {
    type: 'id',
    description: 'ID of the goal being edited',
  },
};

export const EditGoalScreen = ({
  navigation: { goBack },
  route: { params },
}: RootStackScreenProps<'EditGoal'>) => {
  const goal_id = params.id;

  useUpdateScreenChatContext(context);

  const user_id = useCurrentUserID();
  const habitatID = useHabitatID();

  const { state, setGoal, updateGoal, toggleShowPeriods, toggleShowReminders } =
    useGoalFormScreen();

  useUpdateGoalFormCommand(updateGoal);

  const { goal } = useGetGoal(goal_id);
  const remove = useDeleteGoal();
  const update = useUpdateGoal();

  /** Disable name change if goal is being created from a template or was created from a template */
  const disable_name_change = !!(
    goal?.isTemplate || state.goal?.reference_code
  );

  const [alerts, setAlerts] = useState<Alert[]>([]);

  const { data: serverAlerts, isSuccess } = useListAlertsForGoal(
    habitatID,
    goal_id
  );

  const createAlert = useCreateAlertFromModel();
  const updateAlert = useUpdateAlertFromModel();
  const deleteAlert = useDeleteAlert();

  const onSubmit = useSaveCommand(
    useCallback(async () => {
      const { goal } = state;
      if (!goal) return;

      showInfoToast({ text1: 'Saving goal...' });

      // Update reminders
      const { added, removed, updated } = getRecordSetDifference(
        serverAlerts,
        alerts
      );

      await Promise.all([
        createReminders(goal, added),
        deleteReminders(removed),
        updateReminders(updated),
        update(goal),
      ]);

      // If any alerts were changed, added or removed, invalidate the alert query
      if (added.length || removed.length || updated.length) {
        query_client.invalidateQueries(alertQueryKeys.all);
      }

      showSuccessToast({ text1: 'Goal saved' });

      postUpdateRecordLogEvent({
        user_id,
        feature_domain: FeatureDomain.GOALS,
        data: {
          id: goal.id,
          model: 'Goal',
        },
      });

      sendBackgroundMessage(intent_labels.update_goal, {
        context: {
          goal: {
            id: goal.id,
            title: goal.title,
            period: goal.period,
            completions_per_period: goal.completions_per_period,
          },
        },
      });

      goBack();
    }, [state.goal, alerts, serverAlerts, user_id])
  );

  const onDelete = useDeleteCommand(
    useCallback(async () => {
      if (!goal?.id) return;

      showInfoToast({ text1: 'Deleting goal...' });
      goBack();

      await Promise.all([
        remove(goal.id),
        serverAlerts.length ? deleteReminders(serverAlerts) : undefined,
      ]);

      showSuccessToast({ text1: 'Goal deleted' });

      postDeleteRecordLogEvent({
        user_id,
        feature_domain: FeatureDomain.GOALS,
        data: {
          id: goal.id,
          model: 'Goal',
        },
      });

      sendBackgroundMessage(intent_labels.delete_goal, {
        context: {
          goal: {
            id: goal.id,
            title: goal.title,
          },
        },
      });
    }, [goal?.id, user_id, serverAlerts])
  );

  const createReminders = (goal: { id: string }, reminders: Alert[]) =>
    Promise.all(
      reminders.map((reminder) => createAlert({ ...reminder, goalID: goal.id }))
    );

  const deleteReminders = (reminders: Alert[]) =>
    Promise.all(
      reminders.map(({ id }) => deleteAlert.mutateAsync({ input: { id } }))
    );

  const updateReminders = (reminders: Alert[]) =>
    Promise.all(reminders.map((reminder) => updateAlert(reminder)));

  /** Add a reminder */
  const onAddReminder = () =>
    state.goal && setAlerts([...alerts, createGoalReminder(state.goal)]);

  /** Update a reminder */
  const onUpdateReminder = (reminder: Alert) =>
    setAlerts(findAndUpdateRecord(alerts, reminder));

  /** Delete a reminder */
  const onDeleteReminder = ({ id }: Alert) =>
    setAlerts(removeRecordByID(alerts, id));

  /** Creating goal from a template handler */
  useEffect(() => {
    if (!state.goal && goal) {
      setGoal(goal);

      sendBackgroundMessage(intent_labels.open_edit_goal_screen, {
        context: {
          goal: {
            id: goal.id,
            title: goal.title,
            period: goal.period,
            completions_per_period: goal.completions_per_period,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal, state.goal]);

  useEffect(() => {
    if (goal_id && isSuccess) {
      setAlerts(serverAlerts);

      if (serverAlerts?.length) {
        toggleShowReminders();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, goal_id]);

  if (!state.goal) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <GoalFormScreenView
        goal={state.goal}
        reminders={alerts}
        period_visible={state.periods_visible}
        reminders_visible={state.reminders_visible}
        disable_name_change={disable_name_change}
        onChange={setGoal}
        onCancel={goBack}
        onConfirm={onSubmit}
        onDelete={onDelete}
        toggleShowPeriods={toggleShowPeriods}
        toggleShowReminders={toggleShowReminders}
        onAddReminder={onAddReminder}
        onUpdateReminder={onUpdateReminder}
        onDeleteReminder={onDeleteReminder}
        setReminders={setAlerts}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        parameters={<JSONText>{parameters}</JSONText>}
      />
    </>
  );
};
